import { createTheme, Theme } from '@mui/material/styles';
import { lightThemeOptions, lightPaletteColors, lightUiColors, lightColors } from './light';
import { darkThemeOptions, darkPaletteColors, darkUiColors, darkColors } from './dark';

declare module '@mui/material/styles' {
    interface Theme {
        gradients: {
            primary: string;
            secondary: string;
            success: string;
            background: string;
            card: string;
            button: string;
            buttonHover: string;
        };
        landingGradients: {
            primary: string;
            hover: string;
            text: string;
        };
    }

    interface TypographyVariants {
        menu: React.CSSProperties;
    }

    interface TypographyVariantsOptions {
        menu?: React.CSSProperties;
    }

    interface ThemeOptions {
        gradients?: {
            primary?: string;
            secondary?: string;
            success?: string;
            background?: string;
            card?: string;
            button?: string;
            buttonHover?: string;
        };
        landingGradients?: {
            primary?: string;
            hover?: string;
            text?: string;
        };
    }

    interface BreakpointOverrides {
        xxl: true;
    }
}

declare module '@mui/material/Button' {
    interface ButtonPropsVariantOverrides {
        link: true;
        noOutlined: true;
    }
}

declare module '@mui/material/styles' {
    // Add fields for PaletteColor
    interface PaletteColor {
        divider: string;
        hover: string;
        contrastText: string;
    }
}
// Animation and transition configurations
export const animations = {
    transitions: {
        pageFlip: '0.3s ease-in-out',
        zoomIn: '0.3s ease-out',
        fadeIn: '0.4s ease-in-out',
        pulse: '3s ease-in-out infinite',
        ripple: '0.6s ease-out',
    },

    keyframes: {
        pulse: {
            '0%': { transform: 'scale(1)' },
            '50%': { transform: 'scale(1.05)' },
            '100%': { transform: 'scale(1)' },
        },
        fadeIn: {
            '0%': { opacity: 0 },
            '100%': { opacity: 1 },
        },
        zoomIn: {
            '0%': { transform: 'scale(1)' },
            '100%': { transform: 'scale(1.02)' },
        },
        shimmer: {
            '0%': { backgroundPosition: '-200px 0' },
            '100%': { backgroundPosition: 'calc(200px + 100%) 0' },
        },
    },
};

// Create theme function that accepts language and mode parameters
export const createDynamicTheme = (language?: string, mode?: 'dark' | 'light'): Theme => {
    mode = mode || (localStorage.getItem('theme') as 'dark' | 'light' | undefined) || 'light';

    switch (mode) {
        case 'dark':
            return createTheme(darkThemeOptions(language));
        case 'light':
            return createTheme(lightThemeOptions(language));
        default:
            return createTheme(lightThemeOptions(language));
    }
};

// Default theme for backward compatibility (using English/default fonts)
export const theme = createDynamicTheme();

// Export colors based on current mode (evaluated at import time)
export const colors = localStorage.getItem('theme') === 'dark' ? darkColors : lightColors;

// Export all color constants for direct access
export {
    // Light theme colors
    lightPaletteColors,
    lightUiColors,
    lightColors,

    // Dark theme colors
    darkPaletteColors,
    darkUiColors,
    darkColors,

    // Theme options
    lightThemeOptions,
    darkThemeOptions,
};

export default theme;
